import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";
import { Car, Hash, Clock, Eye } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { ROUTES, buildRoute } from "@/constants/routes";
import { useVehicles } from "../hooks/useVehicles";

import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { FilterBar } from "@/components/common/FilterBar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/ui/avatar";

import type { VehicleAPIResponse } from "@/types";
import { vehicleCapacityLabel } from "@/constants/vehicles";

const statusOptions = [
  { value: "approved", label: "Approved" },
  { value: "all", label: "All Status" },
  { value: "pending", label: "Pending" },
  { value: "rejected", label: "Rejected" },
];


function VehicleTypeBadge({ type }: { type?: string }) {
  if (!type) return <span className="text-muted-foreground text-sm">—</span>;
  const label = type.replace(/_/g, " ");
  return (
    <Badge variant="outline" className="capitalize text-xs">
      {label}
    </Badge>
  );
}

function VerificationBadge({ status }: { status: string }) {
  const variant =
    status === "approved"
      ? "success"
      : status === "rejected"
        ? "destructive"
        : "warning";
  return (
    <Badge variant={variant} className="capitalize">
      {status}
    </Badge>
  );
}

export function VehiclesListPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("approved");
  const [search, setSearch] = useState("");

  // Fetch a large batch; client-side filtering is applied inside vehiclesApi.getAll
  const { data, isLoading, isError, refetch } = useVehicles({
    page: 1,
    limit: 100,
    status: status === "all" ? undefined : status,
    search: search || undefined,
  });

  const columns: ColumnDef<VehicleAPIResponse>[] = [
    {
      accessorKey: "driver",
      header: "Driver",
      cell: ({ row }) => {
        const name = row.original.driver?.user?.name || "Unknown";
        const phone =
          row.original.driver?.user?.phone ||
          row.original.driver?.user?.mobile_number;
        return (
          <div className="flex items-center gap-3">
            <Avatar alt={name} size="sm" />
            <div>
              <p className="font-medium">{name}</p>
              {phone && (
                <p className="text-xs text-muted-foreground">{phone}</p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "vehicle_model",
      header: "Model",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Car className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="font-medium">
            {row.original.vehicle_model || "—"}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "registration_number",
      header: "Reg. Number",
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <Hash className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <span className="font-mono font-semibold uppercase text-sm">
            {row.original.registration_number || "—"}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "vehicle_type",
      header: "Type",
      cell: ({ row }) => <VehicleTypeBadge type={row.original.vehicle_type} />,
    },
    {
      accessorKey: "capacity",
      header: "Capacity",
      // Capacity is standardized by vehicle type (not the driver-typed value).
      cell: ({ row }) => (
        <span className="text-sm">{vehicleCapacityLabel(row.original.vehicle_type)}</span>
      ),
    },
    {
      accessorKey: "verification_status",
      header: "Status",
      cell: ({ row }) => (
        <VerificationBadge status={row.original.verification_status} />
      ),
    },
    {
      accessorKey: "created_at",
      header: "Added On",
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          {row.original.created_at ? formatDate(row.original.created_at) : "—"}
        </div>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="outline"
          className="h-8"
          onClick={(e) => {
            e.stopPropagation();
            navigate(buildRoute(ROUTES.VEHICLE_DETAIL, { id: row.original.id }));
          }}
        >
          <Eye className="h-3.5 w-3.5 mr-1" />
          View
        </Button>
      ),
    },
  ];

  if (isError) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Vehicles"
          description="All registered vehicles on the platform"
          actions={
            <Button
              variant="outline"
              onClick={() => navigate(ROUTES.VEHICLE_PENDING)}
            >
              Pending Approvals
            </Button>
          }
        />
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <h3 className="text-lg font-semibold mb-2">Failed to load vehicles</h3>
          <Button onClick={() => refetch()}>Try Again</Button>
        </div>
      </div>
    );
  }

  const vehicles = data?.vehicles ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vehicles"
        description={`${total.toLocaleString()} vehicle${total !== 1 ? "s" : ""} registered on the platform`}
        actions={
          <Button
            variant="outline"
            onClick={() => navigate(ROUTES.VEHICLE_PENDING)}
          >
            Pending Approvals
          </Button>
        }
      />

      <FilterBar
        filters={[
          {
            type: "search",
            key: "search",
            placeholder: "Search by model, reg. number or driver...",
            className: "w-72",
          },
          {
            type: "select",
            key: "status",
            placeholder: "Status",
            options: statusOptions,
          },
        ]}
        values={{ search, status }}
        onChange={(key, value) => {
          if (key === "search") setSearch(value as string);
          if (key === "status") setStatus(value as string);
        }}
        onReset={() => { setSearch(""); setStatus("approved"); }}
        onRefresh={() => refetch()}
      />

      <DataTable
        columns={columns}
        data={vehicles}
        loading={isLoading}
        showPagination={false}
        showSearch={false}
        onRowClick={(row) =>
          navigate(buildRoute(ROUTES.VEHICLE_DETAIL, { id: row.id }))
        }
      />

    </div>
  );
}
